import { t, environmentLabel } from "../locale";
import { useEffect, useRef, useState } from "react";
import {
  
  ArrowUpRight,
  Check,

  Ellipsis,
  Info,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  WifiOff,
  X,
} from "lucide-react";
import { config } from "../config";
import { loadDestination, type DestinationConfig } from "./destination";
import { initializeAnalytics, track } from "./analytics";
import { useViewer } from "../members/Viewer";
import InstallGuide, { isHomeScreen } from "./InstallGuide";
import MarketingConsent from "./MarketingConsent";
import "../members/account.css";

export default function PortalApp() {
  const viewer = useViewer();
  const [saving, setSaving] = useState(false);
  const [destination, setDestination] = useState<DestinationConfig | null>(
    null,
  );
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [frameSlow, setFrameSlow] = useState(false);
  const [dialog, setDialog] = useState<"install" | "about" | null>(null);
  const [standalone, setStandalone] = useState(isHomeScreen);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const openedTarget = useRef("");

  useEffect(() => {
    void initializeAnalytics();
    const onDemoEvent = (event: MessageEvent) => {
      if (
        event.origin !== location.origin ||
        event.source !== iframeRef.current?.contentWindow ||
        event.data?.type !== "yestv:demo"
      )
        return;
      if (["demo_play", "demo_pause", "demo_ended"].includes(event.data.event))
        track(event.data.event);
      if (event.data.event === "demo_ended") viewer.offerRegistration();
    };
    const onAccount = (event: MessageEvent) => {
      if (event.origin !== location.origin || event.source !== iframeRef.current?.contentWindow || event.data?.type !== "yestv:account") return;
      if (event.data.action === "login") void viewer.requestLogin("login");
    };
    addEventListener("message", onDemoEvent);
    addEventListener("message", onAccount);
    return () => { removeEventListener("message", onDemoEvent); removeEventListener("message", onAccount); };
  }, [viewer.user?.registered, viewer.user?.userId]);

  useEffect(() => {
    const target = destination?.destination;
    if (!target || target.demo || target.mode !== "embed") return;
    const origin = new URL(target.url, location.origin).origin;
    const received = new Set<string>();
    const onPlayback = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== iframeRef.current?.contentWindow || event.data?.type !== "yestv:playback" || !["playing", "preview-ended"].includes(event.data?.event)) return;
      const id = event.data.eventId;
      if (typeof id !== "string" || !/^[a-zA-Z0-9_-]{8,100}$/.test(id) || received.has(id)) return;
      if (received.size >= 1000) return;
      received.add(id);
      if (event.data.event === "preview-ended") { viewer.offerRegistration(); return; }
      track("video_play");
    };
    addEventListener("message", onPlayback);
    return () => removeEventListener("message", onPlayback);
  }, [destination, attempt, viewer.user?.registered]);

  useEffect(() => {
    if (
      destination?.enabled &&
      destination.destination.mode === "embed" &&
      openedTarget.current !== destination.revision
    ) {
      openedTarget.current = destination.revision;
      track("video_open");
    }
  }, [destination]);

  useEffect(() => {
    document.title =
      config.environment === "production"
        ? t("Yestv · Watch")
        : t("Yestv · Playback test");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    let active = true;
    setError("");
    setDestination(null);
    setFrameLoaded(false);
    setFrameSlow(false);
    loadDestination(controller.signal)
      .then((value) => {
        if (active) setDestination(value);
      })
      .catch(() => {
        if (active)
          setError(
            navigator.onLine
              ? t("We couldn’t connect to the video website.")
              : t("Connect to the internet to start watching."),
          );
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt]);

  useEffect(() => {
    if (
      !destination ||
      !destination.enabled ||
      destination.destination.mode !== "embed" ||
      frameLoaded
    )
      return;
    const timeout = setTimeout(() => setFrameSlow(true), 12000);
    return () => clearTimeout(timeout);
  }, [destination, frameLoaded]);

  useEffect(() => {
    const updateMode = () => {
      const homeScreen = isHomeScreen();
      setStandalone(homeScreen);
      if (homeScreen)
        setDialog((current) => (current === "install" ? null : current));
    };
    const media = matchMedia("(display-mode: standalone)");
    media.addEventListener("change", updateMode);
    addEventListener("pageshow", updateMode);
    addEventListener("focus", updateMode);
    document.addEventListener("visibilitychange", updateMode);
    return () => {
      media.removeEventListener("change", updateMode);
      removeEventListener("pageshow", updateMode);
      removeEventListener("focus", updateMode);
      document.removeEventListener("visibilitychange", updateMode);
    };
  }, []);

  useEffect(() => {
    if (!dialog) return;
    const element = dialogRef.current;
    element?.showModal();
    return () => element?.close();
  }, [dialog]);

  function showInstallGuide() {
    track("install_prompt");
    setDialog("install");
  }

  const target = destination?.destination;
  const blocked =
    error ||
    (destination && !destination.enabled
      ? t("Yestv is taking a short break. Please try again later.")
      : "");

  return (
    <div className="portal-page">
      <main className="portal-app">
        <header className="portal-header">
          <a className="wordmark" href="/" aria-label={t("Yestv home")}>
            yes<span>tv</span>
            <i>.</i>
          </a>
          <div className="portal-header-actions">
            {viewer.user?.registered ? <a className="viewer-account-link" href="/account">{viewer.user.nickname}</a> : <button className="viewer-account-link" onClick={() => void viewer.requestLogin("login")}>注册 / 登录</button>}
            {config.environment !== "production" && (
              <span className="test-badge">
                <span /> {t("TEST")}
              </span>
            )}
            <button
              className="round-button"
              aria-label={t("About this preview")}
              onClick={() => setDialog("about")}
            >
              <Ellipsis size={22} />
            </button>
          </div>
        </header>

        <MarketingConsent />

        <section className="viewing-stage" aria-label={t("Video website")}>
          {blocked ? (
            <div className="connection-state" role="alert">
              <div className="state-icon">
                <WifiOff size={26} />
              </div>
              <p className="eyebrow">{t("A LITTLE PAUSE")}</p>
              <h1>{t("Let’s reconnect.")}</h1>
              <p>{blocked}</p>
              <button
                className="primary-button"
                onClick={() => setAttempt((value) => value + 1)}
              >
                <RefreshCw size={17} /> {t("Try again")}
              </button>
            </div>
          ) : !destination ? (
            <div className="connection-state" role="status">
              <LoaderCircle className="spin" size={30} />
              <p>{t("Getting your story ready…")}</p>
            </div>
          ) : target?.mode === "navigate" ? (
            <div className="connection-state">
              <div className="state-icon">
                <ArrowUpRight size={28} />
              </div>
              <p className="eyebrow">{t("YOUR NEXT STORY")}</p>
              <h1>{t("Ready when you are.")}</h1>
              <p>
                {t("Continue to")} {destination.name} {t("to start watching.")}
              </p>
              <a
                className="primary-button"
                href={target.url}
                onClick={() => track("video_open")}
              >
                {t("Start watching")} <ArrowUpRight size={18} />
              </a>
            </div>
          ) : target ? (
            <>
              {!frameLoaded && (
                <div className="frame-loading" role="status">
                  <LoaderCircle className="spin" size={28} />
                  <span>{t("Opening your story…")}</span>
                </div>
              )}
              <iframe
                key={`${target.url}:${attempt}`}
                ref={iframeRef}
                className="video-site-frame"
                src={target.url}
                title={target.demo ? t("Yestv · Playback test") : target.title}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => {
                  track("frame_loaded");
                  setFrameLoaded(true);
                  setFrameSlow(false);
                }}
              />
              {frameSlow && (
                <div className="slow-notice">
                  <span>{t("This is taking a little longer.")}</span>
                  <button onClick={() => setAttempt((value) => value + 1)}>
                    {t("Retry")}
                  </button>
                  <a href={target.url} onClick={() => track("video_open")}>
                    {t("Open directly")} <ArrowUpRight size={14} />
                  </a>
                </div>
              )}
            </>
          ) : null}
        </section>

        <footer className="portal-footer">
          <div>
            {!standalone && <button className="viewer-save-button" onClick={showInstallGuide}>添加到桌面</button>}
            {target?.demo && (
              <button
                className="viewer-save-button"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await viewer.changeItem({
                      seriesId: "sintel-preview",
                      title: t("Sintel"),
                      saved: !viewer.items.find(
                        (x) => x.seriesId === "sintel-preview",
                      )?.saved,
                    });
                  } catch {
                    /* Shared sync notice shows the failure. */
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {viewer.items.find((x) => x.seriesId === "sintel-preview")
                  ?.saved
                  ? t("✓ Saved")
                  : t("+ Save story")}
              </button>
            )}
            <span className="footer-status" />
            <span>
              {target?.demo
                ? t("Sample video · Playback preview")
                : t("Your stories, one tap away.")}
            </span>
          </div>
          {standalone && (
            <span className="installed-label">
              <Check size={15} /> {t("On your Home Screen")}
            </span>
          )}
        </footer>
        {viewer.error && (
          <p className="viewer-sync-error" role="status">
            {viewer.error} <a href="/account">{t("My account")}</a>
          </p>
        )}
      </main>

      {dialog && (
        <dialog
          ref={dialogRef}
          className={`portal-dialog${dialog === "install" ? " apple-install-dialog" : ""}`}
          aria-labelledby="portal-dialog-title"
          onCancel={() => setDialog(null)}
          onClick={(event) => {
            if (event.target === event.currentTarget) setDialog(null);
          }}
        >
          <div className="dialog-top">
            <span className="dialog-grip" />
            <button
              className="round-button"
              onClick={() => setDialog(null)}
              aria-label={t("Close dialog")}
            >
              <X size={20} />
            </button>
          </div>
          {dialog === "install" ? (
            <InstallGuide onClose={() => setDialog(null)} />
          ) : (
            <>
              <div className="state-icon">
                <Info size={26} />
              </div>
              <p className="eyebrow">{t("YESTV PREVIEW")}</p>
              <h2 id="portal-dialog-title">{t("Just press play.")}</h2>
              <p>
                {target?.demo
                  ? t(
                      "This preview opens a sample video page so you can try the viewing experience. The existing video website has not been connected yet.",
                    )
                  : t(
                      "Yestv opens the configured video website. Your video account and purchases are managed by that website.",
                    )}
              </p>
              {target?.demo && (
                <div className="preview-facts">
                  <span>
                    <ShieldCheck size={18} />{" "}
                    {t("No sign-in or payment required")}
                  </span>
                  <span>
                    {t("Sample footage: Sintel, © Blender Foundation")}
                  </span>
                  <a
                    href="https://www.sintel.org/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Sintel · CC BY 3.0")} <ArrowUpRight size={14} />
                  </a>
                </div>
              )}
              <p className="dialog-note">
                Yestv {config.version} · {environmentLabel}
              </p>
              {target && (
                <a
                  className="secondary-button full-width"
                  href={target.url}
                  onClick={() => track("video_open")}
                >
                  {t("Open video page directly")} <ArrowUpRight size={16} />
                </a>
              )}
            </>
          )}
        </dialog>
      )}
    </div>
  );
}
